<template>
    <div class="fourSymbolsLineDiagram">
        <div ref="chartEl" :id="'ElephantLine_' + id_type" class="ElephantLine"></div>
    </div>
</template>

<script>
import {
    buildMarkLineData,
    resolveOwnexamGradeKey,
    resolveQuadrantConfig,
    scaleRawXyByGradeK0,
    resolveAxisExtent,
} from './quadrantGradeAxis';

const SCATTER_SYMBOL_SIZE = 12;

const MARK_LINE = {
    symbol: ['none', 'arrow'],
    silent: true,
    lineStyle: { type: 'solid', width: 1, color: '#333' },
};

function buildValueAxis(min, max) {
    return {
        type: 'value',
        min,
        max,
        axisLine: { onZero: false },
        splitLine: { show: false },
        interval: 10,
    };
}

export default {
    props: ['id_type', 'ownexamData', 'username', 'reportType', 'CurrentEvaluationId', 'GradeValue', 'factorMap'],
    data() {
        return {
            chartInstance: null,
            resizeHandler: null,
        };
    },
    computed: {
        gradeValue() {
            const v = this.GradeValue;
            return v != null && v !== '' ? v : null;
        },
        quadrantConfig() {
            return resolveQuadrantConfig(this.gradeValue);
        },
        examList() {
            return Array.isArray(this.ownexamData) ? this.ownexamData : [];
        },
        chartTitleText() {
            const name = this.username != null && this.username !== ''
                ? String(this.username).trim()
                : '';
            const rt = this.reportType != null && this.reportType !== ''
                ? String(this.reportType).trim()
                : '';
            if (name && rt) return `${name} ${rt}`;
            if (name) return name;
            if (rt) return rt;
            return '';
        },
        scatterData() {
            const currentId = this.CurrentEvaluationId;
            const fallback = this.gradeValue;
            return this.examList.map((item, index) => {
                const grade = resolveOwnexamGradeKey(item, fallback);
                const scaled = scaleRawXyByGradeK0(item.x, item.y, grade, this.factorMap);
                const value = scaled ? [scaled.x, scaled.y] : [item.x, item.y];
                const isHighlight = item.test_id === currentId;
                return {
                    value,
                    itemId: item.id,
                    itemName: item.name,
                    createAt: item.create_at,
                    itemStyle: {
                        color: '#5470C6',
                        borderWidth: isHighlight ? 2 : 0,
                    },
                    symbolSize: SCATTER_SYMBOL_SIZE,
                    lineStyle: { type: 'solid', color: '#aaa' },
                    label: {
                        show: true,
                        position: 'top',
                        formatter: () => String(index + 1),
                        fontSize: 13,
                        fontWeight: 'normal',
                        color: '#333',
                    },
                };
            });
        },
        chartOption() {
            const list = this.examList;
            if (!list.length) return null;

            const { axisX, axisY } = this.quadrantConfig;
            const { min, max } = resolveAxisExtent(this.scatterData, axisX, axisY);
            const valueAxis = buildValueAxis(min, max);

            return {
                title: {
                    text: this.chartTitleText,
                    left: 'center',
                    textStyle: { fontWeight: 'normal' },
                },
                tooltip: {
                    show: true,
                    trigger: 'item',
                    formatter: this.formatExamTooltip,
                    position: 'right',
                },
                dataZoom: [{ type: 'inside', startValue: min, endValue: max }],
                grid: { left: '15%', right: '15%' },
                xAxis: valueAxis,
                yAxis: valueAxis,
                series: [{
                    type: 'scatter',
                    data: this.scatterData,
                    markLine: {
                        ...MARK_LINE,
                        data: buildMarkLineData(axisX, axisY),
                    },
                }],
            };
        },
    },
    watch: {
        ownexamData: { handler: 'scheduleInitChart', deep: true },
        CurrentEvaluationId: 'scheduleInitChart',
        reportType: 'scheduleInitChart',
        username: 'scheduleInitChart',
        GradeValue: 'scheduleInitChart',
        factorMap: { handler: 'scheduleInitChart', deep: true },
    },
    mounted() {
        this.scheduleInitChart();
    },
    beforeDestroy() {
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeHandler = null;
        }
        this.disposeChart();
    },
    methods: {
        formatExamTooltip(params) {
            const { itemName, createAt } = params.data;
            const date = this.dayjs(createAt).format('YYYY-MM-DD');
            const effort = Number(params.value[0]).toFixed(2);
            const talent = Number(params.value[1]).toFixed(2);
            return `试卷名称: ${itemName}<br/>努力:${effort}<br/>天赋:${talent}`;
        },
        scheduleInitChart() {
            this.$nextTick(() => this.initChart());
        },
        disposeChart() {
            if (!this.chartInstance) return;
            try {
                this.chartInstance.dispose();
            } catch (e) {
                /* 容器已卸载时可能抛错 */
            }
            this.chartInstance = null;
        },
        bindResizeOnce() {
            if (this.resizeHandler) return;
            this.resizeHandler = () => {
                if (this.chartInstance) {
                    this.chartInstance.resize();
                }
            };
            window.addEventListener('resize', this.resizeHandler);
        },
        initChart() {
            const el = this.$refs.chartEl;
            if (!el) return;

            const option = this.chartOption;
            if (!option) {
                this.disposeChart();
                return;
            }

            if (!this.chartInstance) {
                this.chartInstance = this.$echarts.init(el);
                this.bindResizeOnce();
            }
            this.chartInstance.setOption(option, true);
            this.$nextTick(() => {
                if (this.chartInstance) {
                    this.chartInstance.resize();
                }
            });
        },
    },
};
</script>

<style scoped lang="less">
.fourSymbolsLineDiagram {
    width: 100%;
    height: 100%;

    .ElephantLine {
        width: 100%;
        height: 100%;
    }
}
</style>
